import type { ApplicationAdvancedOptionsType } from '@unifyapps/defs/types/dataSource';
import { isBoolean, isNumber } from 'lodash';
import { useMemo } from 'react';
import type { DataSourceQueryOptions } from '../types';
import {
  BACKOFF_FACTOR_INITIAL_VALUE,
  RETRY_COUNT_INITIAL_VALUE,
  RETRY_DELAY_INITIAL_VALUE,
  type RetryConfig,
} from '../utils/retryConfig';

class QueryOptionsBuilder {
  private options: DataSourceQueryOptions;

  constructor(initialOptions: DataSourceQueryOptions = {}) {
    this.options = { ...initialOptions };
  }

  withTiming(advancedOptions: ApplicationAdvancedOptionsType | undefined) {
    const timing = advancedOptions?.timing;
    if (timing?.runQueryPeriodically && Number(timing.runQueryPeriodInterval) > 0) {
      this.options.refetchInterval = Number(timing.runQueryPeriodInterval);
      // enable periodic refetching even when window is not active
      this.options.refetchIntervalInBackground = true;
    }
    return this;
  }

  withFetchSettings(advancedOptions: ApplicationAdvancedOptionsType | undefined) {
    if (advancedOptions?.refetchOnWindowFocus === false) {
      this.options.refetchOnWindowFocus = false;
    }
    return this;
  }

  withRetry({
    advancedOptions,
    defaultRetryConfig,
  }: {
    advancedOptions: ApplicationAdvancedOptionsType | undefined;
    defaultRetryConfig: RetryConfig;
  }) {
    const retryConfig = advancedOptions ?? {};
    const { retryEnabled, retryCount, retryDelay, retryBackoffFactor } = retryConfig;

    // Explicitly enabled retry with custom config
    if (isBoolean(retryEnabled) && retryEnabled) {
      this.applyCustomRetryConfig({
        retryCount,
        retryDelay,
        retryBackoffFactor,
      });
      return this;
    }

    // Explicitly disabled retry
    if (isBoolean(retryEnabled) && !retryEnabled) {
      this.options.retry = 0;
      return this;
    }

    // Use default retry config when not explicitly configured
    // This would be in scenarios where user has not explicitly enabled retry, so we will use the default retry config that is based on the data source type
    if (retryEnabled === undefined) {
      this.applyDefaultRetryConfig(defaultRetryConfig);
    }

    return this;
  }

  private applyCustomRetryConfig({
    retryCount,
    retryDelay,
    retryBackoffFactor,
  }: {
    retryCount?: number;
    retryDelay?: number;
    retryBackoffFactor?: number;
  }) {
    this.options.retry = isNumber(retryCount) ? retryCount : RETRY_COUNT_INITIAL_VALUE;

    const finalRetryDelay = isNumber(retryDelay) ? retryDelay : RETRY_DELAY_INITIAL_VALUE;
    const finalBackoffFactor = isNumber(retryBackoffFactor)
      ? retryBackoffFactor
      : BACKOFF_FACTOR_INITIAL_VALUE;

    // making sure delay is not more than 30 seconds
    this.options.retryDelay = (attempt) =>
      Math.min(finalRetryDelay * finalBackoffFactor ** attempt, 30000);
  }

  private applyDefaultRetryConfig(defaultRetryConfig: RetryConfig) {
    if (defaultRetryConfig.enabled) {
      this.options.retry = defaultRetryConfig.retryCount;
      // making sure delay is not more than 30 seconds
      this.options.retryDelay = (attempt) =>
        Math.min(defaultRetryConfig.delay * defaultRetryConfig.backoffFactor ** attempt, 30000);
    } else {
      this.options.retry = 0;
    }
  }

  build() {
    return this.options;
  }
}

export function useGenerateQueryOptionsFromAdvancedOptions({
  advancedOptions,
  defaultRetryConfig,
}) {
  return useMemo(() => {
    const queryOptionsBuilder = new QueryOptionsBuilder();
    queryOptionsBuilder.withTiming(advancedOptions);
    queryOptionsBuilder.withFetchSettings(advancedOptions);
    queryOptionsBuilder.withRetry({ advancedOptions, defaultRetryConfig });

    return queryOptionsBuilder.build();
  }, [advancedOptions, defaultRetryConfig]);
}
